import { useState } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Image } from "@/components/wpl/image/src";
import { Link } from "@/components/wpl/link/src";
import { DataEventProps } from "@/libraries/wpl/foundations/src";
import { Play } from "@/libraries/wpl/foundations/src/icons/object/play";
import { YoutubeModal } from "@/patterns/modal/src";

const sectionStyles = css`
    background-color: #deebff;
    display: flex;
`;

const contentStyles = css`
    margin: auto;
    max-width: 1168px;
    width: 96%;
    display: flex;
    padding-top: 80px;
    margin-bottom: 0px;
    padding-bottom: 0px;

    ${media.above.md} {
        padding-left: 90px;
    }

    ${media.above.sm} {
        flex-direction: row;
    }

    ${UNSAFE_media.below.sm} {
        flex-direction: column;
    }
`;

const mainTextStyles = css`
    margin-bottom: 7%;
    width: 90%;

    ${media.above.sm} {
        max-width: 420px;
        margin-right: 6%;
        margin-top: 10%;
    }

    ${UNSAFE_media.below.sm} {
        margin-left: 10%;

        .text-section {
            margin-right: 12%;
        }
    }

    h2 {
        font-size: 2.25rem;
        color: #344563;

        ${UNSAFE_media.below.sm} {
            font-size: 1.5rem;
        }
    }
`;

const linkStyles = css`
    display: flex;
    flex-direction: row;

    ${UNSAFE_media.below.sm} {
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    @media (max-width: 540px) {
        justify-content: start;
        align-items: start;
    }

    a {
        display: flex;
        align-items: center;
        color: #ffffff;
        margin-bottom: ${token("space.150")};

        span {
            font-size: ${token("space.200")};
            color: #0052cc;
        }

        svg {
            fill: #0052cc;
            margin-right: ${token("space.100")};
        }

        :hover {
            span {
                color: #0065ff;
            }

            svg {
                fill: #0065ff;
            }
        }
    }
`;

const appLinkStyles = css`
    display: flex;
    margin-top: ${token("space.600")};
    align-items: center;

    @media (max-width: 365px) {
        flex-direction: column;
        align-items: start;
    }

    a {
        margin-right: ${token("space.150")};
    }
`;

const imageStyles = css`
    max-width: 568px;

    ${media.above.sm} {
        width: 52%;
        height: 52%;
        align-self: flex-end;
    }
`;

const MobileSection = () => {
    const intl = useIntl();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const sectionContent = {
        header: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.header",
            defaultMessage: "Of course we're on mobile"
        }),
        description: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.description",
            defaultMessage:
                "Stay in sync with your team and move work forward – straight from any device."
        }),
        appStoreImageAltText: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.appStoreImageAltText",
            defaultMessage: "Download on the Apple App Store"
        }),
        appStoreText: "Download on the Apple App Store",
        googlePlayImageAltText: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.googlePlayImageAltText",
            defaultMessage: "Get it on Google Play"
        }),
        googlePlayText: "Get it on Google Play",
        mobileImageAltText: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.mobileImageAltText",
            defaultMessage:
                "Confluence mobile app notification screen showing updates and comments that a team has left on multiple pages"
        }),
        learnMore: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.learnMore",
            defaultMessage: "Learn more"
        }),
        videoText: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.videoText",
            defaultMessage: "Watch the video"
        })
    };

    const appStoreImageLink =
        "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:8d670f96-0469-4763-9a3a-92d604d0db76/Download_on_the_App_Store_Badge_US-UK.svg?cdnVersion=1271";
    const googlePlayImageLink =
        "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:ca831509-8569-45d7-921f-76f0984ced2e/google-play-280x86.png?cdnVersion=1271";
    const appStoreLink =
        "https://itunes.apple.com/app/apple-store/id1006971684?pt=696495&ct=wac_1&mt=8";
    const googlePlayLink =
        "https://play.google.com/store/apps/details?id=com.atlassian.android.confluence.core&hl=en";
    const mobileImageLink =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_Mobile.webp";
    const videoLink =
        "https://www.youtube.com/embed/hBWEFTs_bgQ?autoplay=1&rel=0&origin=https%3A%2F%2Fwww.atlassian.com&widget_referrer=https%3A%2F%2Fwww.atlassian.com&enablejsapi=1&widgetid=1";

    const dataEventPropsMap: { [key: string]: DataEventProps } = {
        learnMore: {
            "data-product-key": "confluence",
            "data-event-component": "linkButton",
            "data-event-container": "mobileSection",
            "data-event": "clicked",
            "data-label": sectionContent.learnMore
        },
        videoLink: {
            "data-product-key": "confluence",
            "data-event-component": "linkButton",
            "data-event-container": "videoLink",
            "data-event": "clicked",
            "data-label": sectionContent.videoText,
            "data-label-english": "Watch the video"
        },
        appStoreLink: {
            "data-product-key": "confluence",
            "data-event-component": "linkButton",
            "data-event-container": "mobileSection",
            "data-event": "clicked",
            "data-label": sectionContent.appStoreImageAltText,
            "data-label-english": sectionContent.appStoreText
        },
        googlePlayLink: {
            "data-product-key": "confluence",
            "data-event-component": "linkButton",
            "data-event-container": "mobileSection",
            "data-event": "clicked",
            "data-label": sectionContent.googlePlayImageAltText,
            "data-label-english": sectionContent.googlePlayText
        }
    };

    return (
        <section css={sectionStyles}>
            <div css={contentStyles}>
                <div css={mainTextStyles}>
                    <ContentBlock
                        headerType="h2"
                        headerText={sectionContent.header}
                        bodyText={sectionContent.description}
                    />
                    <div css={linkStyles}>
                        <div
                            css={css`
                                ${media.above.sm} {
                                    margin-right: ${token("space.300")};
                                }
                            `}
                        >
                            <Link
                                text={sectionContent.learnMore}
                                customLinkColor="b400"
                                doesArrowAnimate={true}
                                type="basic-underline"
                                href={useLocalizedHref(
                                    "/software/confluence/mobile-app"
                                )}
                                dataEventProps={dataEventPropsMap["learnMore"]}
                            />
                        </div>
                        <a
                            href={videoLink}
                            {...dataEventPropsMap["videoLink"]}
                            onClick={(event) => {
                                event.preventDefault();
                                setIsModalOpen(true);
                            }}
                        >
                            <Play size="22" />
                            <span>{sectionContent.videoText}</span>
                        </a>
                    </div>
                    <div css={appLinkStyles}>
                        <a
                            href={appStoreLink}
                            {...dataEventPropsMap["appStoreLink"]}
                        >
                            <Image
                                src={appStoreImageLink}
                                alt={sectionContent.appStoreImageAltText}
                                aspectRatio="none"
                                css={css`
                                    width: 130px;
                                    height: 43px;
                                `}
                            />
                        </a>
                        <a
                            href={googlePlayLink}
                            {...dataEventPropsMap["googlePlayLink"]}
                        >
                            <Image
                                src={googlePlayImageLink}
                                alt={sectionContent.googlePlayImageAltText}
                                aspectRatio="none"
                                css={css`
                                    width: 130px;
                                    height: 40px;
                                `}
                            />
                        </a>
                    </div>
                </div>
                <Image
                    src={mobileImageLink}
                    alt={sectionContent.mobileImageAltText}
                    aspectRatio="none"
                    css={imageStyles}
                />
            </div>
            <YoutubeModal
                isOpen={isModalOpen}
                src={videoLink}
                title={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.mobileSection.ModalText",
                    defaultMessage: "Confluence Mobile App | Atlassian"
                })}
                closeIconLabel={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.mobileSection.closeText",
                    defaultMessage: "Close modal"
                })}
                closeModal={() => setIsModalOpen(false)}
            />
        </section>
    );
};

export default MobileSection;
